import { FetchProps } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { AxiosResponse } from 'axios'
import { Powermeter } from '../../../Types/responses/storages/serialNo/hardware/powermeters'
import { StorageDataModel } from '../../../State/Storage/StorageModel'
import { Battery } from '../../../Types/responses/storages/serialNo/hardware/batteries'
import { SystemInfo, WallboxInfo, PowermeterInfo, BatteryInfo, StatObject } from '../models/systemDataStoreModel'
import { majorVersionFromResponse } from '../utils/versions'
import { powermeterTypeMapping } from '../../../Types/responses/powermeter-values/{sn}'
import { objectApiResult } from '@e3dc-react/shell/Libs/ApiHelpers'
import { WallboxHardware } from '@e3dc-react/shell/Libs/EMobility/wallboxes/models/wallbox-hardware.model'
import { WallboxHardwareDto } from 'src/Libs/wallboxes/dto/wallbox-hardware.dto'
import { FarmMember } from 'src/Elements/Farm/types'
import { isFlowTheme } from 'src/Libs/utils'

export const fetchSystemInfo = async (
  serialNo: string,
  authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>,
  isFarmMasterSelected: boolean,
  farmClients: FarmMember[] | undefined
): Promise<SystemInfo | undefined> => {
  const storageStats = authFetch<StorageDataModel>({
    path: `storages/${serialNo}`,
    method: 'GET',
    payload: undefined,
  }).then(r => ({ version: majorVersionFromResponse(r), timezone: r.data.timezone, info: r.data }))

  const historyStats = authFetch<StatObject>({
    path: `storages/${serialNo}/history-values/stats`,
    method: 'GET',
  }).then(r => ({ version: majorVersionFromResponse(r), info: r.data }))

  const clientList = farmClients?.map(client => client.storageId) || []
  const storageIds = isFarmMasterSelected ? clientList : [serialNo]

  let wallboxes
  let storage
  let history
  let powermetersFetched
  let batteriesFetched

  try {
    const wallBoxes = fetchWallBoxStats(serialNo, authFetch)
    const powermeters =
      isFarmMasterSelected && !isFlowTheme ? fetchPowermetersFromMultipleClients(storageIds, authFetch) : fetchPowermeters(serialNo, authFetch)
    const batteries = fetchBatteries(serialNo, authFetch)
    storage = await storageStats
    history = await historyStats
    wallboxes = await wallBoxes
    powermetersFetched = await powermeters
    batteriesFetched = await batteries
  } catch (err) {
    console.error(err)
    return undefined
  }
  return {
    serialNo,
    storage,
    history,
    wallboxes,
    powermeters: powermetersFetched,
    batteries: batteriesFetched,
  }
}

const fetchWallBoxStats = async (serialNo: string, authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>): Promise<WallboxInfo> => {
  try {
    const wallboxesResponse = await authFetch<WallboxHardwareDto>({
      path: `wallboxes/${serialNo}`,
      method: 'GET',
    })

    const version = majorVersionFromResponse(wallboxesResponse)

    const wallboxes = await Promise.all(
      objectApiResult<WallboxHardwareDto, WallboxHardware[]>(wallboxesResponse.data).map(wallbox =>
        authFetch<StatObject>({
          path: `wallboxes/${serialNo}/${wallbox.number}/historydata/stats`,
          method: 'GET',
        }).then(r => ({
          id: wallbox.id,
          info: r.data,
          number: wallbox.number,
        }))
      )
    )

    return {
      version,
      wallboxes,
    }
  } catch (e) {
    console.warn('could not fetch wallbox stats', e)
    return Promise.reject()
  }
}

/**
 * Fetch powermeter information from a single storage.
 */
const fetchPowermeters = async (serialNo: string, authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>): Promise<PowermeterInfo> => {
  const powermetersResponse = await authFetch<Powermeter[]>({
    path: `storages/${serialNo}/hardware/powermeters`,
    method: 'GET',
    payload: undefined,
  })

  const version = majorVersionFromResponse(powermetersResponse)

  const powermeters = await Promise.all(
    powermetersResponse.data.map(powermeter =>
      authFetch<StatObject>({
        path: `storages/${serialNo}/powermeter-values/${powermeter.id}/stats`,
        method: 'GET',
      }).then(r => ({
        id: powermeter.id,
        type: powermeterTypeMapping[powermeter.type?.[1]],
        info: r?.data,
        storageId: serialNo,
        uniqueId: `_${serialNo}_${powermeter.id}`,
      }))
    )
  )
  return {
    version,
    powermeters,
  }
}

/**
 * Fetch powermeter information from farm clients.
 */
const fetchPowermetersFromMultipleClients = async (
  clientsStorageIds: string[],
  authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>
): Promise<PowermeterInfo> => {
  try {
    let version = 0

    const powermeterPromises = clientsStorageIds.map(async clientStorageId => {
      const powermetersResponse = await authFetch<Powermeter[]>({
        path: `storages/${clientStorageId}/hardware/powermeters`,
        method: 'GET',
        payload: undefined,
      })

      version = majorVersionFromResponse(powermetersResponse)

      const powermeters = await Promise.all(
        powermetersResponse.data.map(powermeter =>
          authFetch<StatObject>({
            path: `storages/${clientStorageId}/powermeter-values/${powermeter.id}/stats`,
            method: 'GET',
          }).then(r => ({
            id: powermeter.id,
            type: powermeterTypeMapping[powermeter.type?.[1]],
            info: r?.data,
            storageId: clientStorageId,
            uniqueId: `_${clientStorageId}_${powermeter.id}`,
          }))
        )
      )

      return powermeters
    })

    const allClientPowermeters = await Promise.all(powermeterPromises)
    const flattenedPowermeters = allClientPowermeters.flat()

    return {
      version,
      powermeters: flattenedPowermeters,
    }
  } catch (err) {
    console.error('Error fetching powermeters from multiple clients:', err)
    return {
      version: 0,
      powermeters: [],
    }
  }
}

const fetchBatteries = async (serialNo: string, authFetch: <T>(props: FetchProps) => Promise<AxiosResponse<T>>): Promise<BatteryInfo> => {
  const batteriesResponse = await authFetch<Battery[]>({
    path: `storages/${serialNo}/hardware/batteries`,
    method: 'GET',
    payload: undefined,
  })

  return {
    version: majorVersionFromResponse(batteriesResponse),
    batteries: batteriesResponse.data,
  }
}
